import { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import { doc, onSnapshot } from 'firebase/firestore'
import type { Channel } from 'stream-chat'
import { Mail, ShieldCheck, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useAuth } from '@/hooks/use-auth'
import { useStreamChat } from '@/hooks/use-stream-chat'
import { callableErrorMessage } from '@/lib/callable-errors'
import { handleNameRevealRequest, type NameRevealAction } from '@/lib/callables'
import {
  getChatUserRole,
  parseChatDoc,
  resolveNameRevealInteraction,
  toChatHash,
  toStreamChannelId,
  type ChatDocState,
  type ChatUserRole,
  type NameRevealInteraction,
} from '@/lib/chat'
import { db } from '@/lib/firebase'
import { openVerifyIdentity } from '@/lib/verify-identity-navigation'

const COPY: Record<
  Exclude<NameRevealInteraction, 'none'>,
  { title: string; description: string }
> = {
  makeBuyerRequest: {
    title: "Request owner's name",
    description:
      "Request to see the owner's verified name. If they accept, they'll be able to see your name too.",
  },
  makeOwnerRequest: {
    title: "Request buyer's name",
    description:
      "Request to see the buyer's verified name. They can choose to share their name with you.",
  },
  respondToBuyerRequest: {
    title: 'Buyer requested your name',
    description:
      'The buyer has requested to see your verified name. Choose how you’d like to respond.',
  },
  respondToOwnerRequest: {
    title: 'Owner requested your name',
    description:
      'The owner has requested to see your verified name. Choose how you’d like to respond.',
  },
  ownerVoluntaryShare: {
    title: 'Share your name',
    description:
      'The buyer has already shared their verified name with you. Share yours to reveal it to them.',
  },
  showBuyerSupportContact: {
    title: 'Request declined',
    description:
      "Your request to see the owner's name was declined. Contact support if you need help.",
  },
  showOwnerSupportContact: {
    title: 'Request declined',
    description:
      "Your request to see the buyer's name was declined. Contact support if you need help.",
  },
}

export function NameRevealModal({
  channelId,
  open,
  onClose,
}: {
  channelId: string
  open: boolean
  onClose: () => void
}) {
  const navigate = useNavigate()
  const location = useLocation()
  const { profile } = useAuth()
  const { client, streamId, isReady } = useStreamChat()
  const verified = Boolean(profile?.isIdentityVerified)

  const chatHash = channelId ? toChatHash(channelId) : ''
  const streamChannelId = channelId ? toStreamChannelId(channelId) : ''

  const [channel, setChannel] = useState<Channel | null>(null)
  const [docState, setDocState] = useState<ChatDocState | null>(null)
  const [loading, setLoading] = useState(true)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [frozen, setFrozen] = useState<NameRevealInteraction | null>(null)

  useEffect(() => {
    if (!open) return
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') onClose()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [open, onClose])

  useEffect(() => {
    if (!open || !client || !streamChannelId || !isReady) return
    let cancelled = false
    setLoading(true)
    const ch = client.channel('messaging', streamChannelId)
    void ch
      .watch()
      .then(() => {
        if (!cancelled) setChannel(ch)
      })
      .catch(() => {
        if (!cancelled) setError('Couldn’t load this chat.')
      })
    return () => {
      cancelled = true
    }
  }, [open, client, streamChannelId, isReady])

  useEffect(() => {
    if (!open || !chatHash) {
      setDocState(null)
      setLoading(false)
      return
    }
    setLoading(true)
    const unsub = onSnapshot(
      doc(db, 'chats', chatHash),
      (snap) => {
        setDocState(parseChatDoc(snap.data() as Record<string, unknown> | undefined))
        setLoading(false)
      },
      () => {
        setError('Failed to load name-reveal status.')
        setLoading(false)
      },
    )
    return () => unsub()
  }, [open, chatHash])

  useEffect(() => {
    if (!open) {
      setError(null)
      setFrozen(null)
      setBusy(false)
    }
  }, [open])

  if (!open) return null

  const role: ChatUserRole = getChatUserRole(channel, streamId)
  const liveInteraction = docState
    ? resolveNameRevealInteraction(role, docState)
    : ('none' as NameRevealInteraction)
  const interaction = frozen ?? liveInteraction
  const meta =
    interaction === 'none'
      ? {
          title: 'Names already handled',
          description: 'There’s nothing to do for name reveal on this chat right now.',
        }
      : COPY[interaction]

  async function run(action: NameRevealAction) {
    if (!chatHash || (role !== 'buyer' && role !== 'owner')) {
      setError('Unable to determine your role in this chat.')
      return
    }
    setFrozen(interaction)
    setBusy(true)
    setError(null)
    try {
      await handleNameRevealRequest({
        channelCID: chatHash,
        action,
        userRole: role,
      })
      onClose()
    } catch (e) {
      setError(callableErrorMessage(e))
      setFrozen(null)
    } finally {
      setBusy(false)
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-navy/40 p-4 sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-labelledby="name-reveal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose()
      }}
    >
      <div className="max-h-[90vh] w-full max-w-md overflow-y-auto rounded-2xl bg-white p-6 shadow-xl">
        {!verified ? (
          <div>
            <div className="flex items-start justify-between gap-3">
              <h2 id="name-reveal-title" className="min-w-0 flex-1 text-lg font-bold text-navy">
                Identity verification required
              </h2>
              <button
                type="button"
                onClick={onClose}
                className="-mr-1 -mt-1 shrink-0 rounded-lg p-2 text-muted transition hover:bg-surface"
                aria-label="Close"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <p className="mt-2 text-sm text-muted">
              Name requests require identity verification. Verify once here, then you can request
              a name reveal.
            </p>
            <div className="mt-5 flex flex-col gap-2">
              <Button
                type="button"
                variant="primary"
                className="w-full"
                onClick={() => {
                  onClose()
                  openVerifyIdentity(
                    navigate,
                    location,
                    `${location.pathname}${location.search}`,
                  )
                }}
              >
                Verify identity
              </Button>
              <Button variant="ghost" className="w-full" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        ) : !isReady || loading || !channel ? (
          <div className="flex min-h-[12rem] items-center justify-center">
            <Spinner />
          </div>
        ) : (
          <div>
            <div className="flex items-center gap-3">
              <h2
                id="name-reveal-title"
                className="min-w-0 flex-1 text-lg font-bold leading-snug text-navy"
              >
                {meta.title}
              </h2>
              <div className="flex shrink-0 items-center gap-1">
                <div className="inline-flex items-center gap-1.5 rounded-full bg-success/10 px-2.5 py-1 text-xs font-medium text-success">
                  <ShieldCheck className="h-3.5 w-3.5" />
                  ID Verified
                </div>
                <button
                  type="button"
                  onClick={onClose}
                  className="-mr-1 rounded-lg p-2 text-muted transition hover:bg-surface"
                  aria-label="Close"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>
            <p className="mt-2 text-sm text-muted">{meta.description}</p>

            <div className="mt-5 flex flex-col gap-2">
              {error ? <p className="text-sm text-danger">{error}</p> : null}

              {interaction === 'makeBuyerRequest' || interaction === 'makeOwnerRequest' ? (
                <Button
                  variant="primary"
                  className="w-full"
                  disabled={busy}
                  onClick={() => void run('request')}
                >
                  {busy ? <Spinner className="border-white border-t-transparent" /> : null}
                  Submit request
                </Button>
              ) : null}

              {interaction === 'respondToBuyerRequest' ? (
                <>
                  <Button
                    variant="primary"
                    className="w-full"
                    disabled={busy}
                    onClick={() => void run('accept')}
                  >
                    {busy ? <Spinner className="border-white border-t-transparent" /> : null}
                    Accept (both names revealed)
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={busy}
                    onClick={() => void run('decline')}
                  >
                    Don’t share my name
                  </Button>
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={busy}
                    onClick={() => void run('counter_request')}
                  >
                    Don’t share, but request buyer’s name
                  </Button>
                </>
              ) : null}

              {interaction === 'respondToOwnerRequest' ? (
                <>
                  <Button
                    variant="primary"
                    className="w-full"
                    disabled={busy}
                    onClick={() => void run('accept')}
                  >
                    {busy ? <Spinner className="border-white border-t-transparent" /> : null}
                    Share my name
                  </Button>
                  {docState?.buyerRequestedOwnerName !== 'rejected' ? (
                    <Button
                      variant="outline"
                      className="w-full"
                      disabled={busy}
                      onClick={() => void run('accept_and_request')}
                    >
                      Accept & request owner’s name
                    </Button>
                  ) : null}
                  <Button
                    variant="outline"
                    className="w-full"
                    disabled={busy}
                    onClick={() => void run('decline')}
                  >
                    Don’t share
                  </Button>
                </>
              ) : null}

              {interaction === 'ownerVoluntaryShare' ? (
                <Button
                  variant="primary"
                  className="w-full"
                  disabled={busy}
                  onClick={() => void run('share_name')}
                >
                  {busy ? <Spinner className="border-white border-t-transparent" /> : null}
                  Share my name with the buyer
                </Button>
              ) : null}

              {interaction === 'showBuyerSupportContact' ||
              interaction === 'showOwnerSupportContact' ? (
                <Button asChild variant="primary" className="w-full">
                  <a href="mailto:support@biddle.io?subject=Name%20Request%20Support">
                    <Mail className="h-4 w-4" />
                    Contact support
                  </a>
                </Button>
              ) : null}

              <Button variant="ghost" className="w-full" onClick={onClose}>
                Cancel
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
